<template>
  <div class="restore-account">
    <div class="restore-container">
      <h2>账号恢复</h2>
      <p class="description">如果您的账号已被注销，可以在这里恢复您的账号。</p>
      
      <el-form 
        ref="formRef"
        :model="form"
        :rules="rules"
        label-position="top"
      >
        <el-form-item 
          label="邮箱地址" 
          prop="email"
        >
          <el-input
            v-model="form.email"
            placeholder="请输入注销账号的邮箱地址"
            type="email"
          />
        </el-form-item>
        
        <el-form-item 
          label="验证码" 
          prop="verificationCode"
        >
          <div class="verification-input">
            <el-input
              v-model="form.verificationCode"
              placeholder="请输入验证码"
              maxlength="6"
            >
              <template #append>
                <el-button 
                  :disabled="countdown > 0" 
                  @click="sendVerificationCode"
                >
                  {{ countdown > 0 ? `${countdown}s后重试` : '获取验证码' }}
                </el-button>
              </template>
            </el-input>
          </div>
        </el-form-item>
        
        <div class="form-actions">
          <el-button @click="goBack">返回登录</el-button>
          <el-button 
            type="primary" 
            :loading="restoring"
            @click="handleRestore"
          >
            恢复账号
          </el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import type { FormInstance, FormRules } from 'element-plus';
import axios from 'axios';
import { config } from '@/config/config';

const API_BASE_URL = config.apiBaseUrl;
const router = useRouter();
const formRef = ref<FormInstance>();
const restoring = ref(false);
const countdown = ref(0);

const form = ref({
  email: '',
  verificationCode: ''
});

const rules: FormRules = {
  email: [
    { required: true, message: '请输入邮箱地址', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
  ],
  verificationCode: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { len: 6, message: '验证码长度应为6位', trigger: 'blur' }
  ]
};

const sendVerificationCode = async () => {
  try {
    // 验证邮箱
    await formRef.value?.validateField('email');
    
    const response = await axios.post(
      `${API_BASE_URL}/api/user/send-restore-code?email=${form.value.email}`
    );
    
    if (response.data.success) {
      ElMessage.success('验证码已发送到您的邮箱');
      // 开始倒计时
      countdown.value = 60;
      const timer = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    }
  } catch (error: any) {
    if (error.response?.data?.message) {
      ElMessage.error(error.response.data.message);
    } else if (error.message) {
      ElMessage.error(error.message);
    } else {
      ElMessage.error('发送验证码失败');
    }
  }
};

const handleRestore = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    
    restoring.value = true;
    const response = await axios.post(
      `${API_BASE_URL}/api/user/restore-account?email=${form.value.email}&verificationCode=${form.value.verificationCode}`
    );
    
    if (response.data.success) {
      ElMessage.success('账号已成功恢复，请重新登录');
      router.push('/login');
    }
  } catch (error: any) {
    if (error.response?.data?.message) {
      ElMessage.error(error.response.data.message);
    } else if (error.message) {
      ElMessage.error(error.message);
    } else {
      ElMessage.error('恢复账号失败');
    }
  } finally {
    restoring.value = false;
  }
};

const goBack = () => {
  router.push('/login');
};
</script>

<style lang="scss" scoped>
.restore-account {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f7fa;
  padding: 20px;
  
  .restore-container {
    width: 100%;
    max-width: 440px;
    padding: 40px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
    
    h2 {
      font-size: 24px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
      text-align: center;
    }
    
    .description {
      color: #666;
      font-size: 14px;
      text-align: center;
      margin-bottom: 32px;
    }
    
    .verification-input {
      .el-input-group__append {
        .el-button {
          padding: 0 15px;
        }
      }
    }
    
    .form-actions {
      display: flex;
      justify-content: space-between;
      margin-top: 32px;
      gap: 16px;
      
      .el-button {
        flex: 1;
      }
    }
  }
}
</style> 